import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layers3, Plus, Loader2, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { api, loadKategori } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TABS = [
  { key: "jo", label: "Kategori JO", path: "/kategori" },
  { key: "out", label: "Kategori Pengeluaran", path: "/kategori-pengeluaran" },
];

export const KategoriManager = ({ onChanged }) => {
  const [tab, setTab] = useState("jo");
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const cfg = TABS.find((t) => t.key === tab);
  const path = cfg.path;

  const load = useCallback(async () => {
    const { data } = await api.get(path);
    setRows(data);
    if (path === "/kategori") await loadKategori();
    onChangedRef.current?.();
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return toast.error("Nama kategori wajib diisi");
    setBusy("add");
    try {
      await api.post(path, { name });
      toast.success(`Kategori ${name.toUpperCase()} ditambahkan`);
      setName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah kategori");
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (k) => {
    setBusy(k.name);
    try {
      await api.put(`${path}/${encodeURIComponent(k.name)}`, { active: !k.active });
      toast.success(`${k.name} ${k.active ? "dinonaktifkan" : "diaktifkan"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah kategori");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Hapus kategori ${k.name}?`)) return;
    setBusy(k.name);
    try {
      await api.delete(`${path}/${encodeURIComponent(k.name)}`);
      toast.success(`Kategori ${k.name} dihapus`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus kategori");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="glass rounded-2xl p-6 md:p-8" data-testid="kategori-manager">
      <div className="flex items-center gap-3">
        <Layers3 className="w-5 h-5 text-blue-400" />
        <div>
          <h3 className="font-display text-xl font-semibold">Kelola Kategori</h3>
          <p className="text-xs text-slate-400 mt-1">
            {tab === "jo"
              ? "Kategori nonaktif tidak bisa dipilih pada entri JO baru, tapi data lama tetap tersimpan."
              : "Kategori pengeluaran aktif akan muncul pada dropdown entri Pengeluaran di Laporan Admin."}
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`kategori-tab-${t.key}`}
            onClick={() => {
              setTab(t.key);
              setRows([]);
              setName("");
            }}
            className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${
              tab === t.key
                ? "bg-blue-600 text-white"
                : "bg-slate-900/70 text-slate-300 border border-white/10 hover:text-blue-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Input
          data-testid="kategori-new-input"
          placeholder={tab === "jo" ? "Nama kategori baru (mis. LASSER)" : "Nama kategori pengeluaran (mis. KASBON)"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-11 w-64 bg-slate-900/70 border-white/10"
        />
        <Button
          data-testid="kategori-add-btn"
          onClick={add}
          disabled={busy === "add"}
          className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 px-5"
        >
          {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Tambah</>}
        </Button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((k) => (
          <div
            key={k.name}
            data-testid={`kategori-row-${k.name}`}
            className={`flex items-center justify-between gap-3 rounded-xl border p-4 ${
              k.active ? "border-blue-400/25 bg-blue-500/5" : "border-white/10 bg-slate-900/60"
            }`}
          >
            <div>
              <div className="text-sm font-semibold uppercase tracking-wider text-slate-100">
                {k.name}
              </div>
              <div
                data-testid={`kategori-state-${k.name}`}
                className={`text-[10px] font-semibold uppercase tracking-wider mt-1 ${
                  k.active ? "text-emerald-300" : "text-slate-500"
                }`}
              >
                {k.active ? "aktif" : "nonaktif"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={`kategori-toggle-${k.name}`}
                onClick={() => toggle(k)}
                disabled={busy === k.name}
                title={k.active ? "Nonaktifkan" : "Aktifkan"}
                className="text-slate-300 hover:text-blue-300 disabled:opacity-40 transition-colors duration-200"
              >
                {busy === k.name ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : k.active ? (
                  <ToggleRight className="w-6 h-6 text-emerald-300" />
                ) : (
                  <ToggleLeft className="w-6 h-6" />
                )}
              </button>
              <button
                data-testid={`kategori-delete-${k.name}`}
                onClick={() => remove(k)}
                disabled={busy === k.name}
                title={tab === "jo" ? "Hapus (hanya bila belum dipakai JO)" : "Hapus kategori pengeluaran"}
                className="text-red-300 hover:text-red-200 disabled:opacity-40 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
